import React from 'react';
import { Table, Select, Alert, Checkbox, Tooltip } from 'antd';
import ExportToExcelButton from '@components/ui/ExportToExcelButton';
import type { ColumnsType } from 'antd/es/table';
import styles from './DollarJournalDashboardPage.module.css';
import useDollarJournalDashboardPage from './useDollarJournalDashboardPage';
import PageLayout from '@components/layout/PageLayout';
import { MeterRow } from '@custom-types/dollarJournalDashboard';
import { NSPButton } from '@components/ui/NSPButton';
import { NSPDatePicker } from '@components/ui/NSPDatepicker';
import ApprovalSignatureCell, { APPROVAL_STATUS_OPTIONS, ApprovalColumnHeader } from '@components/ui/ApprovalSignatureCell';
import WarningMessage from '@components/ui/WarningMessage';
import { buildDataColumns } from './columns';

const DollarJournalDashboardPage = () => {
  const {
    monthYearLabel,
    isNextDisabled,
    pickerValue,
    navigateMonth,
    handleDateChange,
    data,
    loading,
    isAdmin,
    isSuperAdmin,
    isManager,
    saving,
    handleEdit,
    handleSave,
    saveFeedback,
    isRowOriginallyApproved,
    username,
    handleExportToExcel,
    exportError,
    getBulkApproveHeaderState,
    handleBulkApproveHeaderChange,
    isSaveDisabled,
    getBulkReadyHeaderState,
    handleBulkReadyHeaderChange,
    isUser,
  } = useDollarJournalDashboardPage();

  // Admins and managers can approve
  const canApprove = isAdmin || isSuperAdmin || isManager;
  // Preparers and super admins can mark ready for approval
  const canMarkReady = isUser || isSuperAdmin;

  // Derived state for the approver bulk header checkbox
  const { checked: bulkHeaderChecked, indeterminate: bulkHeaderIndeterminate } =
    getBulkApproveHeaderState();

  // Derived state for the preparer bulk ready header checkbox
  const { checked: bulkReadyChecked, indeterminate: bulkReadyIndeterminate, disabled: bulkReadyDisabled } =
    getBulkReadyHeaderState();

  const hasAgc = (data ?? []).some((row) => row.meter_type?.toLowerCase() === 'agc wind farm');

  const approvalStatusRank = new Map(APPROVAL_STATUS_OPTIONS.map((o, i) => [o.value, i]));

  const columns: ColumnsType<MeterRow> = [
    // Bulk "Ready for Approval" column — preparer only
    ...(canMarkReady ? [{
      title: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tooltip title={bulkReadyDisabled ? 'All meters are ready' : bulkReadyChecked ? 'Unmark all as ready' : 'Mark all as ready for approval'}>
            <Checkbox
              checked={bulkReadyChecked}
              indeterminate={bulkReadyIndeterminate}
              disabled={bulkReadyDisabled}
              onChange={(e) => handleBulkReadyHeaderChange(e.target.checked)}
            />
          </Tooltip>
          <span>Ready</span>
        </div>
      ),
      key: 'bulk_ready',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, row: MeterRow) => {
        const isApproved = isRowOriginallyApproved(row.meter_computed_id);
        const isReady = row.journal_ready_for_approval === 'Ready';
        return (
          <Checkbox
            checked={isReady}
            disabled={isApproved}
            onChange={(e) => {
              const val = e.target.checked ? 'Ready' : 'In Progress';
              handleEdit(row.meter_computed_id, 'journal_ready_for_approval', val);
              if (!e.target.checked) {
                handleEdit(row.meter_computed_id, 'journal_approved_status', false);
                handleEdit(row.meter_computed_id, 'journal_approver_signature', '');
              }
            }}
          />
        );
      },
    }] as ColumnsType<MeterRow> : []),
    ...buildDataColumns(styles, hasAgc),
    {
      title: 'Approval Status',
      dataIndex: 'journal_ready_for_approval',
      key: 'journal_ready_for_approval',
      width: 145,
      sorter: (a: MeterRow, b: MeterRow) =>
        (approvalStatusRank.get(a.journal_ready_for_approval ?? 'In Progress') ?? 0) -
        (approvalStatusRank.get(b.journal_ready_for_approval ?? 'In Progress') ?? 0),
      sortDirections: ['ascend', 'descend', null] as any,
      render: (_: unknown, row: MeterRow) => {
        const isApproved = isRowOriginallyApproved(row.meter_computed_id);
        return (
          <Select
            size="small"
            value={row.journal_ready_for_approval ?? 'In Progress'}
            style={{ width: 130 }}
            className={isApproved ? styles.approvedStatusSelect : undefined}
            popupMatchSelectWidth={false}
            disabled={isApproved}
            onChange={(val) => {
              handleEdit(row.meter_computed_id, 'journal_ready_for_approval', val);
              if (val !== 'Ready') {
                handleEdit(row.meter_computed_id, 'journal_approved_status', false);
                handleEdit(row.meter_computed_id, 'journal_approver_signature', '');
              }
            }}
            options={canApprove ? APPROVAL_STATUS_OPTIONS : APPROVAL_STATUS_OPTIONS.filter((o) => o.value !== 'Not Approved')}
          />
        );
      },
    },
    {
      title: () => (
        <ApprovalColumnHeader
          canApprove={canApprove}
          checked={bulkHeaderChecked}
          indeterminate={bulkHeaderIndeterminate}
          onChange={handleBulkApproveHeaderChange}
        />
      ),
      key: 'journal_approved_status',
      width: 280,
      sorter: (a: MeterRow, b: MeterRow) =>
        Number(Boolean(a.journal_approved_status)) - Number(Boolean(b.journal_approved_status)),
      sortDirections: ['ascend', 'descend', null] as any,
      render: (_: unknown, row: MeterRow) => {
        const isApproved = isRowOriginallyApproved(row.meter_computed_id);
        const canEdit = canApprove && row.journal_ready_for_approval === 'Ready' && !isApproved;
        return (
          <ApprovalSignatureCell
            approved={Boolean(row.journal_approved_status)}
            signature={row.journal_approver_signature}
            canEdit={canEdit}
            username={username}
            onApprovedChange={(checked) => {
              handleEdit(row.meter_computed_id, 'journal_approved_status', checked);
              if (checked && !row.journal_approver_signature && username) {
                handleEdit(row.meter_computed_id, 'journal_approver_signature', username);
              }
            }}
            onSignatureChange={(val) =>
              handleEdit(row.meter_computed_id, 'journal_approver_signature', val)
            }
          />
        );
      },
    },
  ];

  return (
    <PageLayout
      title={`Dollar Journal for ${monthYearLabel}`}
      headerAccessoryView={
        <div className={styles.periodSelector}>
          <ExportToExcelButton
            onClick={handleExportToExcel}
            style={{ marginRight: 12 }}
          />
          <NSPDatePicker
            picker="month"
            format="MMM YYYY"
            allowClear={false}
            value={pickerValue}
            onChange={handleDateChange}
            showNavigation={true}
            showCalendarIcon={false}
            onNavigateLeft={() => navigateMonth(-1)}
            onNavigateRight={() => navigateMonth(1)}
            isNextDisabled={isNextDisabled}
          />
        </div>
      }
      footerView={
        <NSPButton onClick={handleSave} loading={saving} disabled={isSaveDisabled || saving}>
          Save Changes
        </NSPButton>
      }
    >
      <div className={styles.content}>
        {saveFeedback && (
          <Alert
            message={saveFeedback.status === 'success' ? 'Save Successful!' : 'Save Failed!'}
            description={saveFeedback.message}
            type={saveFeedback.status}
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}
        {exportError && (
          <Alert
            message="Export Failed"
            description={exportError}
            type="error"
            showIcon
            closable
            style={{ marginBottom: 16 }}
          />
        )}
        {/* Highlight meaning banner */}
        {data?.some(row => row.needs_netgen_validation || row.rate_type) && (
          <WarningMessage message="Invoice Net Gen values are highlighted to indicate that Prod Stat Net Gen has been updated after Prod Stat journal approval. Rates are highlighted if the standard contract rate is not being applied." />
        )}


        {/* Table with sticky first 3 columns */}
        <Table<MeterRow>
          dataSource={data}
          columns={columns}
          loading={loading}
          rowKey="meter_computed_id"
          pagination={false}
          scroll={{ x: 'max-content', y: 'calc(100vh - 280px)' }}
          tableLayout="fixed" // respect tight widths + ellipsis
          sortDirections={['ascend', 'descend', null]}
        />
      </div>
    </PageLayout>
  );
};

export default DollarJournalDashboardPage;
